import { Injectable } from '@angular/core';
import { OptionSpotifyItem } from '@mzic/mzic-interfaces';
import { BehaviorSubject } from 'rxjs';

export interface SpotifyConnectionState {
  searchTerm?: string;
  selectedArtist?: OptionSpotifyItem | null;
  lastSearchResults?: OptionSpotifyItem[];
}

@Injectable({
  providedIn: 'root',
})
export class MzicSpotifyConnectionStateService {
  private spotifyStateSource = new BehaviorSubject<SpotifyConnectionState>({});
  public spotifyState$ = this.spotifyStateSource.asObservable();

  constructor() {
    this.initStateFromStorage();
  }

  private initStateFromStorage(): void {
    try {
      const savedState = localStorage.getItem('spotify_connection_state');
      if (savedState) {
        this.spotifyStateSource.next(JSON.parse(savedState));
      }
    } catch (error) {
      console.error('Error recovering Spotify state from localStorage', error);
      this.clearState();
    }
  }

  updateSpotifyState(state: Partial<SpotifyConnectionState>): void {
    const currentState = this.spotifyStateSource.value;
    const updatedState = { ...currentState, ...state };
    this.spotifyStateSource.next(updatedState);
    localStorage.setItem(
      'spotify_connection_state',
      JSON.stringify(updatedState),
    );
  }

  getSpotifyState(): SpotifyConnectionState {
    return this.spotifyStateSource.value;
  }

  setSearchTerm(searchTerm: string): void {
    this.updateSpotifyState({ searchTerm });
  }

  setSelectedArtist(artist: OptionSpotifyItem | null): void {
    this.updateSpotifyState({ selectedArtist: artist });
  }

  setLastSearchResults(results: OptionSpotifyItem[]): void {
    this.updateSpotifyState({ lastSearchResults: results });
  }

  clearState(): void {
    this.spotifyStateSource.next({});
    localStorage.removeItem('spotify_connection_state');
  }

  hasPersistedData(): boolean {
    const state = this.getSpotifyState();
    return !!(
      state.searchTerm ||
      state.selectedArtist ||
      state.lastSearchResults?.length
    );
  }
}
